import React, {useEffect, useState} from 'react';
import Box from '@mui/material/Box';
import {GenericConfigurationForm, FormItem, validateFormData} from 'src/components/GenericConfigurationForm';

interface DifferentialBindingProps {
    formData: any;
    setFormData: (data: any) => void;
    setValidForm: (isValid: boolean) => void;
}

const items: Readonly<FormItem>[] = [
    {
        name: 'significance_threshold',
        humanReadableName: 'Significance threshold',
        description: 'Decide which peaks are included in BED files based on their significance.',
        longDescription:
            "Trovomics reports all binding sites detected in the analysis, whether they're statistically significant or not. However, when we generate BED files (used for visualization or downstream tools), we include only the peaks that pass the significance threshold. In other words: All peaks will be listed in the full results table. Only significant peaks will be included in the BED files, ensuring regions in your BED files are statistically reliable and biologically meaningful.",
        numbers: {
            min: 0,
            max: 1,
            step: 0.01
        }
    },
    {
        name: 'summits',
        humanReadableName: 'Summits',
        description:
            'Control how all peaks are centered and resized prior to differential binding analysis. Set to -1 to retain original peak widths.',
        longDescription:
            'Expected range: 200–500. This value is passed to DiffBind dba.count function. Creates a fixed width around the summit (point of enrichment) of each peak. A value of 200 means ±200 around the summit. If set to 0, summits will be calculated, but peaksets will be unaffected. If set to -1 (a placeholder for "summits=FALSE"), the original, possibly variable, peak widths will be retained.',
        numbers: {
            min: -1,
            max: 1000,
            step: 1
        }
    },
    {
        name: 'tss_region',
        humanReadableName: 'Transcription Start Site (TSS) region',
        description:
            'Distance from TSS for annotation of differentially bound peaks. Defines a TSS region of [-tss_region, tss_region] around the TSS.',
        longDescription:
            'Distance from the transcription start site (TSS) to consider for peak annotation. Setting TSS region to [-3000, 3000] means that any peak located within 3000 base pairs upstream or downstream of a gene\'s TSS will be considered part of its promoter region and labeled accordingly in the annotation output. When annotating peaks, a genomic feature is assigned to each peak, such as Promoter, Exon, Intron, UTR, or Intergenic regions. If a peak falls within the specified tssRegion window, it will be labeled as "Promoter". This parameter enables researchers to customize the definition of a promoter based on their biological context. Note that the tssRegion parameter does not filter the annotated peak file; it simply assigns the appropriate label based on the peak\'s location relative to the TSS region defined by this parameter.',
        numbers: {
            min: 0,
            max: 10000,
            step: 1
        }
    },
    {
        name: 'level',
        humanReadableName: 'Level',
        description:
            'Control the level at which gene features are considered during annotation, impacting the level of detail provided in the output.',
        longDescription:
            '"transcript" means the annotation focuses on individual transcripts within a gene. If a peak overlaps with a specific Exon, Intron, or UTR of a particular transcript, the output will indicate this; "gene" will associate peaks with the overall gene entity, providing gene-level information rather than transcript-specific details',
        selects: [
            {value: 'transcript', label: 'Transcript'},
            {value: 'gene', label: 'Gene'}
        ]
    },
    {
        name: 'overlap',
        humanReadableName: 'Overlap',
        description: 'Overlap type for peak annotation of differentially bound peaks.',
        longDescription:
            'Dictates how overlapping genomic features are handled during peak annotation. When a peak overlaps multiple genomic regions, this determines the primary annotation. "TSS" (Transcription Start Site) annotates with the gene closest to, or overlapping with, the peak. "All" reports all genes within a certain distance, regardless of TSS overlap.',
        selects: [
            {value: 'tss', label: 'TSS'},
            {value: 'all', label: 'All'}
        ]
    },
    {
        name: 'genomic_annotation_priority',
        humanReadableName: 'Genomic annotation priority',
        description: 'Define the order of priority for genomic annotations when multiple annotations overlap with a peak.',
        longDescription:
            'Sometimes, a single ChIP seq peak might overlap with multiple genomic features, e.g., a peak could be within an exon, but also near a Transcription Start Site (TSS). To ensure a consistent and meaningful annotation, the following list decides which genomic feature to assign to the peak in such overlapping cases.',
        list_items: [
            {value: 'Promoter', label: 'Promoter'},
            {value: '5UTR', label: '5UTR'},
            {value: '3UTR', label: '3UTR'},
            {value: 'Exon', label: 'Exon'},
            {value: 'Intron', label: 'Intron'},
            {value: 'Downstream', label: 'Downstream'},
            {value: 'Intergenic', label: 'Intergenic'}
        ]
    }
];

const DifferentialBindingParameters = ({formData, setFormData, setValidForm}: DifferentialBindingProps) => {
    const handleInputChange = (values: Record<string, any>) => {
        setFormData((prevData: any) => ({
            ...prevData,
            differential_binding_analysis: {
                ...prevData.differential_binding_analysis,
                ...values
            }
        }));
    };

    const [errors, setErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        const newErrors = validateFormData(formData.differential_binding_analysis, items);
        setErrors(newErrors);
        setValidForm(Object.keys(newErrors).length === 0);
    }, [formData, setValidForm]);

    return (
        <Box
            p={3}
            my={1}
            sx={{borderRadius: '10px', bgcolor: 'surface-container-lowest', display: 'flex', flexDirection: 'column', gap: 3}}>
            <GenericConfigurationForm
                items={items}
                itemValues={formData.differential_binding_analysis}
                setItemValues={handleInputChange}
                itemErrors={errors}
            />
        </Box>
    );
};

export default DifferentialBindingParameters;
